import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, Upload } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { RadioGroup, RadioGroupItem } from 'components/ui/radio-group';
import { Textarea } from 'components/ui/textarea';
import { useToast } from 'hooks/use-toast';
import { useImportGraphql } from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import { gptChatService } from 'features/chat/agent-chat-module/services/gpt-chat.service';
import { NotificationType } from 'features/chat/types/notification-types';
import { useNotificationStore } from 'state/store/common/useNotificationStore';
import { useConfigStore } from 'state/store/config/useConfigStore';
import { useTranslation } from 'react-i18next';

type ImportGraphqlModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  toolId: string;
  projectKey: string;
};

type Phase = 'input' | 'importing' | 'done';
type Source = 'file' | 'url';

interface IngestResult {
  success?: boolean;
  status?: string;
  found?: number;
  converted?: number;
  skipped?: number | Array<{ op?: string; reason?: string }>;
  error?: string;
}

const DEFAULT_DEPTH = 2;

export function ImportGraphqlModal({ open, onOpenChange, toolId, projectKey }: ImportGraphqlModalProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { mutateAsync: importGraphql, isPending } = useImportGraphql();
  const blocksKey = useConfigStore((state) => state.configurationSettings?.blocksKey || '');
  // Completion is driven by the same real-time MCP_INGEST notification as the Swagger import.
  const notification = useNotificationStore((state) => state.notification);

  const [phase, setPhase] = useState<Phase>('input');
  const [source, setSource] = useState<Source>('file');
  const [sdl, setSdl] = useState('');
  const [schemaUrl, setSchemaUrl] = useState('');
  const [graphqlUrl, setGraphqlUrl] = useState('');
  const [depth, setDepth] = useState(DEFAULT_DEPTH);
  const [ingestResult, setIngestResult] = useState<IngestResult | null>(null);
  // Dedupe guard: the store only holds the latest message (mirrors import-swagger-modal).
  const lastPayloadRef = useRef<string | null>(null);

  const isFailed = ingestResult ? ingestResult.success === false || ingestResult.status === 'failed' : false;

  useEffect(() => {
    if (!open) {
      setPhase('input');
      setSource('file');
      setSdl('');
      setSchemaUrl('');
      setGraphqlUrl('');
      setDepth(DEFAULT_DEPTH);
      setIngestResult(null);
    }
  }, [open]);

  useEffect(() => {
    const raw = notification?.message?.denormalizedPayload;
    if (!raw || typeof raw !== 'string' || lastPayloadRef.current === raw) return;
    lastPayloadRef.current = raw;

    let cancelled = false;
    const applyResult = async () => {
      try {
      const payload = JSON.parse(raw);
      if (payload?.type !== NotificationType.MCP_INGEST || payload?.file_id !== toolId) return;

      let result: IngestResult = {
        success: payload.success,
        status: payload.status,
        found: payload.found,
        converted: payload.converted,
        skipped: payload.skipped,
        error: payload.error,
      };
      // Some deployed notification paths can report terminal success before
      // carrying the persisted counters. Re-read the tool report before showing
      // a misleading 0/0/0 result.
      if (
        payload.success === true &&
        (payload.found == null || payload.converted == null || payload.skipped == null ||
          (payload.found === 0 && payload.converted === 0 && payload.skipped === 0)) &&
        blocksKey
      ) {
        const response = await gptChatService.getToolById(toolId, projectKey, blocksKey);
        const persisted = response.ingest_report ?? response.IngestReport ?? response.tool?.ingest_report ?? response.tool?.IngestReport;
        if (persisted?.status) {
          result = { success: persisted.status !== 'failed', status: persisted.status, found: persisted.found, converted: persisted.converted, skipped: persisted.skipped, error: persisted.error };
        }
      }
      if (cancelled) return;
      setIngestResult(result);
      setPhase('done');
      // Refresh the tool detail so the API tab shows the imported operations.
      void queryClient.invalidateQueries({ queryKey: ['tools', 'detail', toolId, projectKey] });
      } catch {
        /* ignore unrelated / malformed notifications */
      }
    };
    void applyResult();
    return () => {
      cancelled = true;
    };
  }, [notification, toolId, projectKey, blocksKey, queryClient]);

  const handleFile = async (file?: File | null) => {
    if (!file) return;
    const text = await file.text();
    setSdl(text);
    setSource('file');
  };

  const canSubmit = source === 'file' ? sdl.trim().length > 0 : schemaUrl.trim().length > 0;

  const handleImport = async () => {
    try {
      // Ignore any notification already in the store; only react to this import's completion.
      lastPayloadRef.current = notification?.message?.denormalizedPayload ?? null;
      await importGraphql({
        toolId,
        payload:
          source === 'file'
            ? { source: 'file', sdl, ...(graphqlUrl.trim() && { graphqlUrl: graphqlUrl.trim() }), depth }
            : {
                source: 'url',
                schemaUrl: schemaUrl.trim(),
                ...(graphqlUrl.trim() && { graphqlUrl: graphqlUrl.trim() }),
                depth,
              },
      });
      setIngestResult(null);
      setPhase('importing');
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('IMPORT_FAILED') || 'Import failed',
        description: error instanceof Error ? error.message : t('SOMETHING_WENT_WRONG') || 'Something went wrong.',
      });
    }
  };

  const skippedList = Array.isArray(ingestResult?.skipped) ? ingestResult!.skipped : [];
  const skippedCount = Array.isArray(ingestResult?.skipped)
    ? ingestResult!.skipped.length
    : typeof ingestResult?.skipped === 'number'
      ? ingestResult.skipped
      : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-2xl flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>{t('IMPORT_FROM_GRAPHQL') || 'Import from GraphQL'}</DialogTitle>
          <DialogDescription>
            {t('IMPORT_FROM_GRAPHQL_DESCRIPTION') ||
              'Generate API actions from a GraphQL schema. Each operation is imported as an editable action.'}
          </DialogDescription>
        </DialogHeader>

        {phase === 'input' && (
          <div className="flex flex-col gap-4 overflow-y-auto">
            <RadioGroup
              value={source}
              onValueChange={(value) => setSource(value as Source)}
              className="flex gap-6"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="file" id="graphql-source-file" />
                <Label htmlFor="graphql-source-file" className="font-normal">
                  {t('UPLOAD_SCHEMA_FILE') || 'Upload schema file'}
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="url" id="graphql-source-url" />
                <Label htmlFor="graphql-source-url" className="font-normal">
                  Fetch schema from URL
                </Label>
              </div>
            </RadioGroup>

            {source === 'file' ? (
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="graphql-sdl">{t('SCHEMA_SDL') || 'Schema (SDL)'}</Label>
                  <label className="inline-flex cursor-pointer items-center gap-1.5 text-sm">
                    <Upload className="h-4 w-4" />
                    {t('UPLOAD_FILE') || 'Upload file'}
                    <input
                      type="file"
                      accept=".graphql,.graphqls,.gql,.txt"
                      className="hidden"
                      onChange={(event) => handleFile(event.target.files?.[0])}
                    />
                  </label>
                </div>
                <Textarea
                  id="graphql-sdl"
                  value={sdl}
                  onChange={(event) => setSdl(event.target.value)}
                  className="min-h-[240px] resize-none font-mono text-sm"
                  spellCheck={false}
                  placeholder={'type Query {\n  user(id: ID!): User\n}'}
                />
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <Label htmlFor="graphql-schema-url">Schema URL</Label>
                <Input
                  id="graphql-schema-url"
                  type="url"
                  value={schemaUrl}
                  onChange={(event) => setSchemaUrl(event.target.value)}
                  placeholder="https://example.com/schema.graphql"
                />
                <p className="text-xs text-muted-foreground">
                  The URL must return GraphQL SDL or an introspection JSON document. It must be publicly readable.
                </p>
              </div>
            )}

            <div className="flex flex-col gap-2 sm:max-w-2xl">
              <Label htmlFor="graphql-action-url">GraphQL request URL (optional)</Label>
              <Input
                id="graphql-action-url"
                type="text"
                value={graphqlUrl}
                onChange={(event) => setGraphqlUrl(event.target.value)}
                placeholder="/data/v4/gateway or https://api.example.com/graphql"
              />
              <p className="text-xs text-muted-foreground">
                Used as the endpoint for generated actions. Defaults to the tool path or /graphql.
              </p>
            </div>

            <div className="flex flex-col gap-2 sm:max-w-[200px]">
              <Label htmlFor="graphql-depth">{t('DEPTH') || 'Depth'}</Label>
              <Input
                id="graphql-depth"
                type="number"
                min={1}
                value={depth}
                onChange={(event) => setDepth(Math.max(1, Number(event.target.value) || DEFAULT_DEPTH))}
              />
              <p className="text-xs text-muted-foreground">
                {t('GRAPHQL_DEPTH_HINT') || 'Nested-field expansion depth for generated operations.'}
              </p>
            </div>
          </div>
        )}

        {phase === 'importing' && (
          <div className="flex h-[280px] flex-col items-center justify-center gap-3 text-center">
            <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
            <p className="text-sm font-medium">{t('IMPORTING_SCHEMA') || 'Importing schema...'}</p>
            <p className="max-w-md text-sm text-muted-foreground">
              {t('IMPORTING_SCHEMA_DESCRIPTION') ||
                'Generating operations from the schema. This may take a moment.'}
            </p>
          </div>
        )}

        {phase === 'done' && isFailed && (
          <div className="flex flex-col gap-3 overflow-y-auto">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              <p className="text-sm font-medium">{t('IMPORT_FAILED') || 'Import failed'}</p>
            </div>
            <p className="text-sm text-muted-foreground">
              {ingestResult?.error || t('IMPORT_FAILED_DESCRIPTION') || 'The schema could not be imported.'}
            </p>
          </div>
        )}

        {phase === 'done' && !isFailed && (
          <div className="flex flex-col gap-4 overflow-y-auto">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
              <p className="text-sm font-medium">{t('IMPORT_COMPLETE') || 'Import complete'}</p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg border border-border p-3 text-center">
                <p className="text-lg font-semibold">{ingestResult?.found ?? 0}</p>
                <p className="text-xs text-muted-foreground">{t('FOUND') || 'Found'}</p>
              </div>
              <div className="rounded-lg border border-border p-3 text-center">
                <p className="text-lg font-semibold">{ingestResult?.converted ?? 0}</p>
                <p className="text-xs text-muted-foreground">{t('CONVERTED') || 'Converted'}</p>
              </div>
              <div className="rounded-lg border border-border p-3 text-center">
                <p className="text-lg font-semibold">{skippedCount}</p>
                <p className="text-xs text-muted-foreground">{t('SKIPPED') || 'Skipped'}</p>
              </div>
            </div>
            {skippedList.length > 0 && (
              <div className="max-h-40 overflow-auto rounded-lg border border-border p-3">
                <p className="mb-2 text-xs font-medium text-muted-foreground">{t('SKIPPED') || 'Skipped'}</p>
                <ul className="space-y-1 text-xs text-muted-foreground">
                  {skippedList.map((item, index) => (
                    <li key={`${item.op ?? 'op'}-${index}`}>
                      <span className="font-medium text-foreground">{item.op || '—'}</span>
                      {item.reason ? ` — ${item.reason}` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          {phase === 'input' ? (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isPending}>
                {t('CANCEL') || 'Cancel'}
              </Button>
              <Button onClick={handleImport} disabled={!canSubmit || isPending}>
                {isPending ? t('IMPORTING') || 'Importing...' : t('IMPORT') || 'Import'}
              </Button>
            </>
          ) : (
            <Button onClick={() => onOpenChange(false)} disabled={phase === 'importing'}>
              {phase === 'importing' ? t('IMPORTING') || 'Importing...' : t('DONE') || 'Done'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
